import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { useSelector } from 'react-redux'
import AnimatedTableChevron from 'src/components/AnimatedTableChevron/AnimatedTableChevron'
import ObjectTable from 'src/components/ObjectTable/ObjectTable'
import ObjectTableBodyCell from 'src/components/ObjectTableBodyCell/ObjectTableBodyCell'
import ObjectTableBodyRow from 'src/components/ObjectTableBodyRow/ObjectTableBodyRow'
import ReceiptsTooltip from 'src/components/ReceiptsTooltip/ReceiptsTooltip'
import {
  rollupStatusFromAttachments,
  verificationFromMatchingStatuses,
} from 'src/models/receiptVerification.models'
import { TransactionListModel } from 'src/models/transaction.models'
import { transactionDetailUrl } from 'src/navigation/urls'
import { orgReceiptManagementQueries } from 'src/queries/organizations/receiptManagement/receiptManagement.queries'
import { transactionsQueryKeys } from 'src/queries/queries.constants'
import { queryClient } from 'src/queryClient'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import styled, { css, CSSProp } from 'styled-components'

type TransactionsListMediumProps = {
  transactions: TransactionListModel[]
}

const StyledBodyCell = styled(ObjectTableBodyCell)<{
  width: string
  smWidth?: string
}>`
  ${({ width }): CSSProp => {
    return css`
      width: ${width};
    `
  }}
  ${({ smWidth }): CSSProp => {
    return smWidth
      ? css`
          ${getBreakpointDown(breakpointSizes.SM)(css`
            width: ${smWidth};
          `)}
        `
      : css``
  }}
`

const StyledSpanAllModifiers = styled.span<{
  transaction: TransactionListModel
  defaultFontSize: number
  creditFontSize?: number
  reversalFontSize?: number
}>`
  ${({
    transaction,
    defaultFontSize,
    creditFontSize,
    reversalFontSize,
  }): CSSProp => {
    return transaction.isPending
      ? css`
          ${getFontCss(
            defaultFontSize,
            styleConstants.colors.gray_dark,
            styleConstants.fontFamilies.regular,
          )};
        `
      : transaction.isReversal
        ? css`
            ${getFontCss(
              reversalFontSize || defaultFontSize,
              styleConstants.colors.gray_dark,
              styleConstants.fontFamilies.regular,
            )};
          `
        : transaction.isDeclined
          ? css`
              ${getFontCss(
                defaultFontSize,
                styleConstants.colors.red,
                styleConstants.fontFamilies.regular,
              )};
            `
          : transaction.isCredit
            ? css`
                ${getFontCss(
                  creditFontSize || defaultFontSize,
                  themeConstants.colors.highlight,
                  styleConstants.fontFamilies.regular,
                )};
              `
            : css`
                ${getFontCss(
                  defaultFontSize,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
              `
  }}
`

const TransactionsListMedium: React.FC<TransactionsListMediumProps> = (
  props,
): React.ReactElement => {
  const user = useSelector(selectUserListModelFromState)
  const { data: receiptManagementEnabled } = useQuery(
    orgReceiptManagementQueries.enabled(user?.organizationId),
  )

  return (
    <ObjectTable>
      {props.transactions.map((transaction, i) => (
        <ObjectTableBodyRow
          key={i}
          to={{
            pathname: transactionDetailUrl(transaction.id),
            search: '',
            hash: '',
            state: {},
          }}
          ariaLabelSource={'Transaction Details'}
          onClick={() => {
            if (transaction.isSplit) {
              queryClient.removeQueries({
                queryKey: transactionsQueryKeys.detail(transaction.id),
              })
            }
          }}
        >
          <StyledBodyCell
            width={'30%'}
            css={css`
              border-top: 1px solid transparent;
              ${getFontCss(
                14,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              ${transaction.isPending &&
              css`
                ${getFontCss(
                  14,
                  styleConstants.colors.gray_dark,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            `}
          >
            {transaction.dateString}
          </StyledBodyCell>
          <StyledBodyCell width={'26%'} smWidth={'22%'}>
            <div
              css={css`
                display: flex;
                flex-direction: column;
              `}
            >
              <span
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                  ${transaction.isPending &&
                  css`
                    ${getFontCss(
                      14,
                      styleConstants.colors.gray_dark,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                `}
              >
                {transaction.merchantNameOrUnknown}
              </span>
              <span
                css={css`
                  margin-top: 4px;
                  ${getFontCss(
                    12,
                    styleConstants.colors.gray_darkest,
                    styleConstants.fontFamilies.medium,
                  )};
                  ${transaction.isPending &&
                  css`
                    ${getFontCss(
                      12,
                      styleConstants.colors.gray_dark,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                `}
              >
                {transaction.mccGroupIfKnownMerchant}
              </span>
            </div>
          </StyledBodyCell>

          <StyledBodyCell
            width={'28%'}
            css={css`
              text-align: right;
              white-space: nowrap;
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-end;
              `}
            >
              <StyledSpanAllModifiers
                transaction={transaction}
                defaultFontSize={14}
              >
                {transaction.clearingOrAuthLabel()}
              </StyledSpanAllModifiers>
              {!transaction.isNonCreditCleared && (
                <StyledSpanAllModifiers
                  transaction={transaction}
                  defaultFontSize={12}
                  reversalFontSize={14}
                  creditFontSize={14}
                >
                  {transaction.pseudoStatusLabel}
                </StyledSpanAllModifiers>
              )}
            </div>
          </StyledBodyCell>
          {/* receipt reminder icon */}
          <StyledBodyCell width={'6%'} smWidth={'24%'} noRightPadding>
            <div
              css={css`
                position: absolute;
                z-index: 1;
              `}
            >
              {transaction.attachmentsCount !== undefined &&
                transaction.attachmentsCount > 0 && (
                  <ReceiptsTooltip
                    count={transaction.attachmentsCount}
                    placement={'right'}
                    transactionId={transaction.id}
                    verificationStatus={
                      transaction.receiptMatchingStatus ??
                      rollupStatusFromAttachments(transaction.attachments) ??
                      verificationFromMatchingStatuses(
                        transaction.receiptMatchingStatuses,
                      )?.status
                    }
                    verificationReasons={
                      verificationFromMatchingStatuses(
                        transaction.receiptMatchingStatuses,
                      )?.reasons
                    }
                  />
                )}
              {receiptManagementEnabled &&
                transaction.attachmentsCount === 0 &&
                transaction.receiptRequired && (
                  <ReceiptsTooltip placement={'right'} isMissing />
                )}
            </div>
          </StyledBodyCell>
          <StyledBodyCell
            width={'6%'}
            css={css`
              ${transaction.isPending &&
              css`
                opacity: 1;
              `}
            `}
          >
            <AnimatedTableChevron />
          </StyledBodyCell>
        </ObjectTableBodyRow>
      ))}
    </ObjectTable>
  )
}

export default TransactionsListMedium
